import errorReporter from '@app/errorReporter';
import { NonAgentJsonType } from '@bb/domain/dist/types';
import { Flex, Text } from '@brokerbay/elements-web';
import type { InputRef } from 'antd';
import {
  Button,
  Input,
  message,
  Required,
  Select,
  SelectOption,
} from 'components';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { getPhoneInputMask } from 'utils';
import saveNonAgent from '../../../../../api/NonAgentController/save';
import updateNonAgent from '../../../../../api/NonAgentController/update';
import { nonAgentTypes } from '../constants';
import CoopSelectContext from './CoopSelectContext';

export type NonAgentCreateProps = {
  onNonAgentSubmit: (c: NonAgentJsonType) => void;
  onSearchRoster: () => void;
};

const NonAgentCreate: React.FC<NonAgentCreateProps> = ({
  onNonAgentSubmit,
  onSearchRoster,
}) => {
  const { coop, coopAdditionalData } = useContext(CoopSelectContext);
  const isEdit = Boolean(coop && coopAdditionalData?.coopType === 'nonAgent');

  const [nonAgent, setNonAgent] = useState<
    Pick<
      NonAgentJsonType,
      | 'type'
      | 'firstName'
      | 'lastName'
      | 'email'
      | 'phone'
      | 'companyName'
      | 'companyPhone'
      | 'companyAddress'
    >
  >(() => {
    if (isEdit && coopAdditionalData!.coopType === 'nonAgent') {
      return {
        type: coopAdditionalData!.nonAgentType,
        firstName: coop!.firstName!,
        lastName: coop!.lastName!,
        email: coop!.email,
        phone: coop!.phone,
        companyName: coopAdditionalData!.orgName,
        companyPhone: coopAdditionalData!.orgPhone,
        companyAddress: coopAdditionalData!.orgAddress,
      };
    }
    return {
      type: 'Appraisal',
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companyPhone: '',
      companyAddress: '',
    };
  });
  const [errorMessages, setErrorMessages] = useState<
    Record<Exclude<keyof typeof nonAgent, 'companyAddress' | 'type'>, string>
  >({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    companyName: '',
    companyPhone: '',
  });
  const phoneRef: React.ForwardedRef<InputRef> = useRef<InputRef>(null);
  const companyPhoneRef: React.ForwardedRef<InputRef> = useRef<InputRef>(null);

  useEffect(() => {
    if (phoneRef.current && companyPhoneRef.current) {
      getPhoneInputMask().mask([
        phoneRef.current.input!,
        companyPhoneRef.current.input!,
      ]);
    }
  }, []);

  const handleSubmit = async () => {
    const { firstName, lastName, phone, email, companyName, companyPhone } =
      nonAgent;
    const newErrorMessages: typeof errorMessages = {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      companyName: '',
      companyPhone: '',
    };
    let errorCount = 0;
    if (!firstName) {
      newErrorMessages.firstName = 'First name is required.';
      errorCount += 1;
    }
    if (!lastName) {
      newErrorMessages.lastName = 'Last name required.';
      errorCount += 1;
    }
    if (phone && !phoneRef.current?.input?.inputmask?.isValid()) {
      newErrorMessages.phone = 'Invalid phone number';
      errorCount += 1;
    }
    if (!companyPhone) {
      newErrorMessages.companyPhone = 'Company phone is required.';
      errorCount += 1;
    } else if (!companyPhoneRef.current?.input?.inputmask?.isValid()) {
      newErrorMessages.companyPhone = 'Invalid phone number';
      errorCount += 1;
    }
    if (!email) {
      newErrorMessages.email = 'Email is required.';
      errorCount += 1;
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)) {
      newErrorMessages.email = 'Invalid email address.';
      errorCount += 1;
    }
    if (!companyName) {
      newErrorMessages.companyName = 'Company name is required.';
      errorCount += 1;
    }
    setErrorMessages(newErrorMessages);
    if (errorCount) {
      message.warning(
        `At least ${errorCount} field${
          errorCount !== 1 ? 's need to be revised' : ' needs to be revised'
        }`,
      );
      return;
    }

    try {
      let createdNonAgent: Awaited<ReturnType<typeof saveNonAgent>>;
      if (isEdit) {
        createdNonAgent = await updateNonAgent(nonAgent, coop!.id!);
      } else {
        createdNonAgent = await saveNonAgent(nonAgent);
      }
      onNonAgentSubmit(createdNonAgent);
    } catch (err) {
      errorReporter.error(err);
      message.error(`Failed to ${isEdit ? 'update' : 'save'} custom contact`);
    }
  };

  const onChange = (
    fieldName: keyof typeof nonAgent,
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { value } = e.currentTarget;
    setNonAgent(prev => ({ ...prev, [fieldName]: value }));
  };

  return (
    <StyledFlex vertical gap="middle">
      <div>
        <Text>Showing Type</Text>
        <StyledSelect
          value={nonAgent.type}
          data-cy="inPersonShowingNonAgent-select-type"
          onChange={type => setNonAgent(prev => ({ ...prev, type }))}
          showSearch
        >
          {nonAgentTypes.map(type => (
            <SelectOption
              key={type}
              value={type}
              data-cy={`inPersonShowingNonAgent-type-${type}`}
            >
              {type}
            </SelectOption>
          ))}
        </StyledSelect>
      </div>
      <Flex justify="space-between">
        <Text>
          <Required /> = Required field
        </Text>
        <Button
          size="small"
          type="text"
          data-cy="NonAgentCreate-searchRoster-btn"
          onClick={onSearchRoster}
        >
          Search Roster
        </Button>
      </Flex>
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.firstName || ''}
          label="First Name"
          onChange={e => onChange('firstName', e)}
          value={nonAgent.firstName}
        />
        <Input
          required
          error={errorMessages.lastName || ''}
          label="Last Name"
          onChange={e => onChange('lastName', e)}
          value={nonAgent.lastName}
        />
      </Flex>
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.email || ''}
          label="Email"
          onChange={e => onChange('email', e)}
          value={nonAgent.email}
        />
        <Input
          error={errorMessages.phone || ''}
          label="Mobile"
          ref={phoneRef}
          placeholder="Phone"
          onChange={e => onChange('phone', e)}
          value={nonAgent.phone}
        />
      </Flex>
      <Input
        required
        error={errorMessages.companyName || ''}
        label="Company Name"
        onChange={e => onChange('companyName', e)}
        value={nonAgent.companyName}
      />
      <Flex justify="space-between" gap="large">
        <Input
          required
          error={errorMessages.companyPhone || ''}
          ref={companyPhoneRef}
          label="Company Phone"
          onChange={e => onChange('companyPhone', e)}
          value={nonAgent.companyPhone}
        />
        <Input
          label="Company Address"
          onChange={e => onChange('companyAddress', e)}
          value={nonAgent.companyAddress}
        />
      </Flex>
      <Text>Listing agent to confirm approval for this contact</Text>
      <Flex justify="flex-end" gap="middle">
        <Button
          type="primary"
          data-cy="nonAgentCreate-save-btn"
          onClick={handleSubmit}
        >
          {isEdit ? 'Update' : 'Save'} Profile
        </Button>
        <Button
          type="default"
          data-cy="nonAgentCreate-cancel-btn"
          onClick={onSearchRoster}
        >
          Cancel
        </Button>
      </Flex>
    </StyledFlex>
  );
};

const StyledSelect = styled(Select)`
  margin-bottom: 8px;
  .ant-select > .ant-select-selector {
    background-color: #fff;
    border: 1px solid #e4e5e7;
  }
`;

export const StyledFlex = styled(Flex)`
  margin-top: 16px;
`;

export default NonAgentCreate;
